import { System } from '../System';
import { Entity } from '../Entity';
import { TransformComponent } from '../components/TransformComponent';
import { worldToScreen, lerp, TILE_WIDTH, TILE_HEIGHT } from '../../utils/IsometricUtils';

/**
 * CameraSystem - Smooth camera following for the player
 * Implements lerp-based following with deadzone
 */
export class CameraSystem extends System {
  public readonly requiredComponents = ['transform'];

  /** Entity ID to follow */
  private targetEntityId: string = 'player';

  /** Smooth follow speed (0-1, higher = faster) */
  private lerpFactor: number = 0.1;

  /** Deadzone - camera won't move if target is within this area */
  private deadzone = { width: 100, height: 75 };

  /** Map offset (used when no tilemap) */
  private mapOffsetX: number = 0;
  private mapOffsetY: number = 0;

  /** Tilemap layer for coordinate conversion */
  private groundLayer: Phaser.Tilemaps.TilemapLayer | null = null;

  /** Whether camera has been initialized */
  private initialized: boolean = false;

  public init(): void {
    const camera = this.world.getScene().cameras.main;
    this.mapOffsetX = camera.width / 2;
    this.mapOffsetY = 200;
  }

  /**
   * Set the entity to follow
   */
  public setTarget(entityId: string): void {
    this.targetEntityId = entityId;
    this.initialized = false; // Reset to snap to new target
  }

  /**
   * Set the map offset (used when no tilemap)
   */
  public setMapOffset(x: number, y: number): void {
    this.mapOffsetX = x;
    this.mapOffsetY = y;
  }

  /**
   * Set tilemap layer for coordinate conversion
   */
  public setTilemapLayer(layer: Phaser.Tilemaps.TilemapLayer | null): void {
    this.groundLayer = layer;
  }

  /**
   * Configure camera follow parameters
   */
  public configure(options: {
    lerpFactor?: number;
    deadzone?: { width: number; height: number };
  }): void {
    if (options.lerpFactor !== undefined) {
      this.lerpFactor = Math.max(0.01, Math.min(1, options.lerpFactor));
    }
    if (options.deadzone) {
      this.deadzone = options.deadzone;
    }
  }

  /**
   * Convert world coordinates to screen position
   * Uses tilemap methods when available for proper alignment
   */
  private worldToScreenPos(worldX: number, worldY: number, worldZ: number = 0): { x: number; y: number } {
    if (this.groundLayer) {
      const pos = this.groundLayer.tileToWorldXY(worldX, worldY);
      if (pos) {
        return {
          x: pos.x + TILE_WIDTH / 2,
          y: pos.y + TILE_HEIGHT / 2 - worldZ,
        };
      }
    }
    // Fallback
    const screenPos = worldToScreen(worldX, worldY, worldZ);
    return {
      x: screenPos.x + this.mapOffsetX,
      y: screenPos.y + this.mapOffsetY,
    };
  }

  public update(entities: Entity[], _delta: number): void {
    // Find target entity
    const target = entities.find((e) => e.id === this.targetEntityId || e.hasTag('player'));
    if (!target) return;

    const transform = target.getComponent<TransformComponent>('transform');
    if (!transform) return;

    // Convert world position to screen position
    const targetPos = this.worldToScreenPos(transform.x, transform.y, transform.z);
    const targetScreenX = targetPos.x;
    const targetScreenY = targetPos.y;

    // Initialize camera position on first update (snap to target)
    if (!this.initialized) {
      const camera = this.world.getScene().cameras.main;
      camera.centerOn(targetScreenX, targetScreenY);
      this.initialized = true;
      return;
    }

    const camera = this.world.getScene().cameras.main;

    // Calculate distance from camera center to target
    const cameraX = camera.scrollX + camera.width / 2;
    const cameraY = camera.scrollY + camera.height / 2;

    const dx = targetScreenX - cameraX;
    const dy = targetScreenY - cameraY;

    // Only move camera if target is outside deadzone
    let moveX = 0;
    let moveY = 0;

    if (Math.abs(dx) > this.deadzone.width / 2) {
      // Target is outside horizontal deadzone
      const sign = dx > 0 ? 1 : -1;
      const overshoot = Math.abs(dx) - this.deadzone.width / 2;
      moveX = sign * overshoot;
    }

    if (Math.abs(dy) > this.deadzone.height / 2) {
      // Target is outside vertical deadzone
      const sign = dy > 0 ? 1 : -1;
      const overshoot = Math.abs(dy) - this.deadzone.height / 2;
      moveY = sign * overshoot;
    }

    // Apply smooth interpolation
    if (moveX !== 0 || moveY !== 0) {
      const newScrollX = lerp(camera.scrollX, camera.scrollX + moveX, this.lerpFactor);
      const newScrollY = lerp(camera.scrollY, camera.scrollY + moveY, this.lerpFactor);

      camera.setScroll(newScrollX, newScrollY);
    }
  }

  /**
   * Instantly center camera on target
   */
  public snapToTarget(): void {
    const target = this.world.getEntity(this.targetEntityId);
    if (!target) return;

    const transform = target.getComponent<TransformComponent>('transform');
    if (!transform) return;

    const targetPos = this.worldToScreenPos(transform.x, transform.y, transform.z);
    const camera = this.world.getScene().cameras.main;
    camera.centerOn(targetPos.x, targetPos.y);
  }

  public destroy(): void {
    // No cleanup needed
  }
}
